/**
 * Moon Energy — Product Page JS
 * Handles variant selection, image carousel, size guide, add-to-cart
 */
(function () {
  'use strict';

  const productData = window.__moonEnergyProduct;
  if (!productData) return;

  const variants = productData.variants;
  const options = productData.options;
  const images = productData.images || [];

  // ---- State ----
  let selectedOptions = {};
  options.forEach((opt, i) => {
    const firstAvailable = variants.find(v => v.available) || variants[0];
    if (firstAvailable) {
      selectedOptions[opt] = firstAvailable['option' + (i + 1)];
    }
  });

  // ---- Srcset helper ----
  // Shopify CDN URLs from img_url carry a size+crop suffix, e.g.:
  //   image_2000x2000.jpg?v=123
  //   image_2000x2000_crop_center.jpg?v=123
  // Strip the entire size+crop block before inserting the requested size so we
  // never produce double-suffixed URLs like _2000x2000_crop_center_800x.jpg.
  function shopifySizedUrl(src, size) {
    return src.replace(
      /(?:_\d+x\d*(?:_crop_\w+)?)?(\.(?:jpg|jpeg|gif|png|webp))(\?.*)?$/i,
      '_' + size + '$1$2'
    );
  }

  function buildSrcset(src) {
    if (!src) return '';
    return shopifySizedUrl(src, '800x') + ' 800w, ' + shopifySizedUrl(src, '2000x') + ' 2000w';
  }

  // ---- Carousel state ----
  let currentImageIndex = 0;
  const pdImageContainer = document.getElementById('pd-image-container');

  function setCarouselImage(index) {
    if (!images.length) return;
    currentImageIndex = ((index % images.length) + images.length) % images.length;
    const mainImg = document.getElementById('pd-main-image');
    if (mainImg && images[currentImageIndex]) {
      mainImg.src = shopifySizedUrl(images[currentImageIndex].src, '2000x');
      mainImg.srcset = buildSrcset(images[currentImageIndex].src);
      mainImg.alt = images[currentImageIndex].alt || '';
    }
    // Update active thumbnail
    document.querySelectorAll('.pd-thumb').forEach(t => {
      t.classList.toggle('active', parseInt(t.dataset.index) === currentImageIndex);
    });
    preloadAdjacent(currentImageIndex);
  }

  // Prev/next arrow handlers
  const prevBtn = document.getElementById('pd-prev-btn');
  const nextBtn = document.getElementById('pd-next-btn');
  if (prevBtn) prevBtn.addEventListener('click', () => setCarouselImage(currentImageIndex - 1));
  if (nextBtn) nextBtn.addEventListener('click', () => setCarouselImage(currentImageIndex + 1));

  // Thumbnail click handlers
  document.querySelectorAll('.pd-thumb[data-index]').forEach(thumb => {
    thumb.addEventListener('click', function () {
      const idx = parseInt(this.dataset.index);
      setCarouselImage(isNaN(idx) ? 0 : idx);
    });
  });

  // ---- "View all" gallery overlay (shown when a product has more than 3 images) ----
  const viewAllBtn = document.getElementById('pd-view-all-btn');
  const galleryOverlay = document.getElementById('pd-gallery-overlay');
  const galleryClose = document.getElementById('pd-gallery-close');

  function openGalleryOverlay() {
    if (!galleryOverlay) return;
    galleryOverlay.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }

  function closeGalleryOverlay() {
    if (!galleryOverlay) return;
    galleryOverlay.style.display = 'none';
    document.body.style.overflow = '';
  }

  if (viewAllBtn) viewAllBtn.addEventListener('click', openGalleryOverlay);
  if (galleryClose) galleryClose.addEventListener('click', closeGalleryOverlay);
  if (galleryOverlay) {
    galleryOverlay.addEventListener('click', function (e) {
      if (e.target === galleryOverlay) closeGalleryOverlay();
    });
    galleryOverlay.querySelectorAll('.pd-gallery-item').forEach(item => {
      item.addEventListener('click', function () {
        const idx = parseInt(this.dataset.index);
        closeGalleryOverlay();
        openLightbox(isNaN(idx) ? 0 : idx);
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && galleryOverlay.style.display === 'flex') closeGalleryOverlay();
    });
  }

  // ---- Preload adjacent images for snappier nav ----
  const preloadedSrcs = new Set();
  function preloadImage(src) {
    if (!src || preloadedSrcs.has(src)) return;
    preloadedSrcs.add(src);
    const img = new Image();
    img.src = shopifySizedUrl(src, '2000x');
  }
  function preloadAdjacent(index) {
    if (images.length <= 1) return;
    const next = images[(index + 1) % images.length];
    const prev = images[(index - 1 + images.length) % images.length];
    if (next) preloadImage(next.src);
    if (prev) preloadImage(prev.src);
  }
  preloadAdjacent(currentImageIndex);

  // ---- Swipe left/right on main product image ----
  const SWIPE_THRESHOLD = 40;
  let mainSwiped = false;
  if (pdImageContainer) {
    let mainTouchStartX = null;
    let mainTouchStartY = null;
    pdImageContainer.addEventListener('touchstart', function (e) {
      if (images.length <= 1) return;
      mainTouchStartX = e.touches[0].clientX;
      mainTouchStartY = e.touches[0].clientY;
      mainSwiped = false;
    }, { passive: true });
    pdImageContainer.addEventListener('touchmove', function (e) {
      if (mainTouchStartX == null) return;
      const dx = e.touches[0].clientX - mainTouchStartX;
      const dy = e.touches[0].clientY - mainTouchStartY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) mainSwiped = true;
    }, { passive: true });
    pdImageContainer.addEventListener('touchend', function (e) {
      if (mainTouchStartX == null) return;
      const dx = e.changedTouches[0].clientX - mainTouchStartX;
      const dy = e.changedTouches[0].clientY - mainTouchStartY;
      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        setCarouselImage(currentImageIndex + (dx < 0 ? 1 : -1));
      }
      mainTouchStartX = null;
      mainTouchStartY = null;
    }, { passive: true });
  }

  // ---- Get matching variant ----
  function getCurrentVariant() {
    return variants.find(v =>
      options.every((opt, i) => v['option' + (i + 1)] === selectedOptions[opt])
    ) || null;
  }

  function isValueReachable(optionIdx, value) {
    const optKey = 'option' + (optionIdx + 1);
    return variants.some(v => {
      if (v[optKey] !== value) return false;
      if (!v.available) return false;
      return options.every((_, i) => {
        if (i === optionIdx) return true;
        return v['option' + (i + 1)] === selectedOptions[options[i]];
      });
    });
  }

  function updateAddToCartBtn(variant) {
    const addBtn = document.getElementById('add-to-cart-btn');
    if (!addBtn) return;
    if (!variant) {
      addBtn.disabled = true;
      addBtn.textContent = 'Select Options';
      addBtn.classList.remove('added');
    } else if (variant.available) {
      addBtn.disabled = false;
      addBtn.textContent = 'Add to Cart';
      addBtn.classList.remove('added');
    } else {
      addBtn.disabled = true;
      addBtn.textContent = 'Sold Out';
      addBtn.classList.remove('added');
    }
  }

  function renderSelectors() {
    options.forEach((opt, idx) => {
      const isSize = opt.toLowerCase().includes('size');
      const isColor = opt.toLowerCase().includes('color') || opt.toLowerCase().includes('colour');

      if (isSize) {
        document.querySelectorAll('.pd-size-btn').forEach(btn => {
          if (btn.dataset.option !== opt) return;
          const val = btn.dataset.value;
          const reachable = isValueReachable(idx, val);
          btn.classList.toggle('active', selectedOptions[opt] === val);
          btn.classList.toggle('unavailable', !reachable);
        });
      } else if (isColor) {
        const colorDisplay = document.getElementById('color-display');
        if (colorDisplay) colorDisplay.textContent = selectedOptions[opt] || 'Select';
        document.querySelectorAll('.pd-dropdown-item').forEach(item => {
          if (item.dataset.option !== opt) return;
          const val = item.dataset.value;
          const reachable = isValueReachable(idx, val);
          item.classList.toggle('active', selectedOptions[opt] === val);
          if (reachable) {
            item.classList.remove('unavailable-option');
            item.style.opacity = '';
            item.style.textDecoration = '';
            item.style.cursor = '';
          } else {
            item.classList.add('unavailable-option');
            item.style.opacity = '0.4';
            item.style.textDecoration = 'line-through';
            item.style.cursor = 'not-allowed';
          }
        });
      } else {
        document.querySelectorAll('.pd-size-btn').forEach(btn => {
          if (btn.dataset.option !== opt) return;
          const val = btn.dataset.value;
          const reachable = isValueReachable(idx, val);
          btn.classList.toggle('active', selectedOptions[opt] === val);
          btn.classList.toggle('unavailable', !reachable);
        });
      }
    });

    const variant = getCurrentVariant();

    // If variant has its own image, show it in carousel
    if (variant && variant.featured_image) {
      const mainImg = document.getElementById('pd-main-image');
      if (mainImg) {
        mainImg.src = shopifySizedUrl(variant.featured_image.src, '2000x');
        mainImg.srcset = buildSrcset(variant.featured_image.src);
      }
    }

    const variantInput = document.getElementById('variant-id-input');
    if (variantInput && variant) variantInput.value = variant.id;

    updateAddToCartBtn(variant);
  }

  // ---- Size button handlers ----
  document.querySelectorAll('.pd-size-btn').forEach(btn => {
    btn.addEventListener('click', function () {
      if (this.classList.contains('unavailable')) return;
      selectedOptions[this.dataset.option] = this.dataset.value;
      renderSelectors();
    });
  });

  // ---- Color dropdown ----
  const colorDropdownToggle = document.getElementById('color-dropdown-toggle');
  const colorDropdownMenu = document.getElementById('color-dropdown-menu');
  const colorDropdownArrow = document.getElementById('color-dropdown-arrow');

  if (colorDropdownToggle) {
    colorDropdownToggle.addEventListener('click', function () {
      const isOpen = colorDropdownMenu && colorDropdownMenu.style.display === 'block';
      if (colorDropdownMenu) colorDropdownMenu.style.display = isOpen ? 'none' : 'block';
      if (colorDropdownArrow) colorDropdownArrow.classList.toggle('open', !isOpen);
    });
  }

  document.querySelectorAll('.pd-dropdown-item').forEach(item => {
    item.addEventListener('click', function () {
      if (this.classList.contains('unavailable-option')) return;
      selectedOptions[this.dataset.option] = this.dataset.value;
      if (colorDropdownMenu) colorDropdownMenu.style.display = 'none';
      if (colorDropdownArrow) colorDropdownArrow.classList.remove('open');
      renderSelectors();
    });
  });

  document.addEventListener('click', function (e) {
    if (!colorDropdownMenu || !colorDropdownToggle) return;
    if (!colorDropdownToggle.contains(e.target) && !colorDropdownMenu.contains(e.target)) {
      colorDropdownMenu.style.display = 'none';
      if (colorDropdownArrow) colorDropdownArrow.classList.remove('open');
    }
  });

  // ---- Quantity ----
  let quantity = 1;
  const qtyDisplay = document.getElementById('qty-display');
  document.getElementById('qty-decrement')?.addEventListener('click', () => {
    if (quantity > 1) { quantity--; if (qtyDisplay) qtyDisplay.textContent = quantity; }
  });
  document.getElementById('qty-increment')?.addEventListener('click', () => {
    quantity++;
    if (qtyDisplay) qtyDisplay.textContent = quantity;
  });

  // ---- Add to cart ----
  const addBtn = document.getElementById('add-to-cart-btn');
  if (addBtn) {
    addBtn.addEventListener('click', async function () {
      const variant = getCurrentVariant();
      if (!variant || !variant.available) return;
      addBtn.disabled = true;
      addBtn.textContent = 'Adding...';
      await window.addToCart(variant.id, quantity);
      addBtn.textContent = 'Added!';
      addBtn.classList.add('added');
      setTimeout(() => {
        addBtn.disabled = false;
        addBtn.textContent = 'Add to Cart';
        addBtn.classList.remove('added');
      }, 1500);
    });
  }

  // ---- Size guide ----
  const sizeGuideBtn = document.getElementById('size-guide-btn');
  const sizeGuideEl = document.getElementById('size-guide');
  if (sizeGuideBtn && sizeGuideEl) {
    sizeGuideBtn.addEventListener('click', () => {
      sizeGuideEl.style.display = sizeGuideEl.style.display === 'block' ? 'none' : 'block';
    });
  }

  // ---- Auto-extract Tapstitch size chart from description into Size Guide panel ----

  // Tapstitch (and similar size-chart apps) frequently bake per-cell inline
  // styles (style="background:#fff;color:#000" or bgcolor/color attributes)
  // straight into the table markup so it looks right on a light page. Those
  // inline styles have higher specificity than our stylesheet rules, so on
  // our dark theme they render as invisible light-on-light / dark-on-dark
  // text. Strip every inline style/color/background attribute recursively so
  // only our CSS classes control appearance.
  function stripInlineTableStyles(el) {
    el.removeAttribute('style');
    el.removeAttribute('bgcolor');
    el.removeAttribute('color');
    el.removeAttribute('background');
    for (var i = 0; i < el.children.length; i++) {
      stripInlineTableStyles(el.children[i]);
    }
  }

  var sizeChartExtracted = false;

  function extractSizeChartFromDescription() {
    if (sizeChartExtracted) return true;

    var descFull = document.querySelector('.pd-description-full');
    var sizeGuide = document.getElementById('size-guide');
    if (!descFull || !sizeGuide) return false;

    var table = descFull.querySelector('table');
    if (!table) return false;

    // Walk UP from the table to find the direct child of descFull that contains it.
    // This hides the table AND any "Size Chart" heading text inside the same container,
    // regardless of how deeply the table is nested or what tag the heading uses.
    var tableAncestor = table;
    while (tableAncestor.parentElement && tableAncestor.parentElement !== descFull) {
      tableAncestor = tableAncestor.parentElement;
    }
    tableAncestor.style.display = 'none';

    // Also hide the immediately preceding sibling of the container if it contains
    // "size chart" text (handles cases where heading is a separate sibling element).
    var prev = tableAncestor.previousElementSibling;
    if (prev) {
      var prevTxt = prev.textContent.trim().toLowerCase();
      if (prevTxt.length < 100 && (prevTxt.includes('size chart') || prevTxt.includes('size guide'))) {
        prev.style.display = 'none';
      }
    }

    // Nuclear backup: scan every element (*) in the description — no tag filter.
    // Catches any tag type (h5, h6, section, span, etc.) with short "size chart"
    // or "size guide" text.
    descFull.querySelectorAll('*').forEach(function(el) {
      if (el.style.display === 'none') return;
      var txt = el.textContent.trim().toLowerCase();
      if (txt.length < 50 && (txt.startsWith('size chart') || txt.startsWith('size guide'))) {
        el.style.display = 'none';
      }
    });

    // Clone the original <table> (not tableAncestor) so we capture just the data,
    // then strip Tapstitch's baked-in inline colors so our dark-theme CSS applies.
    var tableClone = table.cloneNode(true);
    tableClone.style.display = '';
    stripInlineTableStyles(tableClone);

    var wrap = document.createElement('div');
    wrap.className = 'pd-measurements-table';
    wrap.appendChild(tableClone);

    // Preserve the panel's h3 heading, replace the rest with the extracted table.
    var h3 = sizeGuide.querySelector('h3');
    sizeGuide.innerHTML = '';
    if (h3) sizeGuide.appendChild(h3);
    sizeGuide.appendChild(wrap);

    sizeChartExtracted = true;
    return true;
  }

  // Tapstitch's size-chart widget can inject its table into the description
  // asynchronously (after our own deferred script has already run once), so a
  // single call at page-load can silently find nothing and give up. Watch the
  // description for DOM changes and retry extraction whenever content is
  // added, until it succeeds. Also poll briefly as a belt-and-suspenders
  // fallback for environments where mutation events might be missed.
  function watchForSizeChart() {
    var descFull = document.querySelector('.pd-description-full');
    if (!descFull) return;
    if (extractSizeChartFromDescription()) return;

    var observer = new MutationObserver(function () {
      if (extractSizeChartFromDescription() && observer) {
        observer.disconnect();
      }
    });
    observer.observe(descFull, { childList: true, subtree: true });
    observer.observe(document.body, { childList: true, subtree: true });

    // Poll indefinitely (not just for a few seconds) since some size-chart apps
    // load their own script asynchronously and can take much longer than 5s to
    // inject their table, especially on slow connections or when their app
    // fetches remote config before rendering. Slow the poll interval down over
    // time so it doesn't burn CPU forever, but never give up outright.
    var attempts = 0;
    var pollId = null;
    function scheduleNextPoll() {
      attempts++;
      var delay = attempts < 20 ? 250 : 2000;
      pollId = setTimeout(function () {
        if (extractSizeChartFromDescription()) {
          observer.disconnect();
          return;
        }
        scheduleNextPoll();
      }, delay);
    }
    scheduleNextPoll();
  }

  // ---- Back button ----
  document.getElementById('pd-back-btn')?.addEventListener('click', () => {
    window.location.href = window.routes && window.routes.collections_url ? window.routes.collections_url : '/collections/all';
  });

  // ---- Lightbox ----
  const lightbox = document.getElementById('pd-lightbox');
  const lightboxImg = document.getElementById('pd-lightbox-img');
  const lightboxInner = document.getElementById('pd-lightbox-inner');
  const lightboxClose = document.getElementById('pd-lightbox-close');
  const lightboxPrev = document.getElementById('pd-lightbox-prev');
  const lightboxNext = document.getElementById('pd-lightbox-next');
  const lightboxHint = document.getElementById('pd-lightbox-hint');
  let lightboxIndex = 0;
  let hintTimeout = null;

  function setLightboxImage(index) {
    if (!images.length) return;
    lightboxIndex = ((index % images.length) + images.length) % images.length;
    const img = images[lightboxIndex];
    if (img && lightboxImg) {
      lightboxImg.src = shopifySizedUrl(img.src, '2000x');
      lightboxImg.alt = img.alt || '';
    }
    if (lightbox) lightbox.classList.remove('zoomed');
    const showArrows = images.length > 1;
    if (lightboxPrev) lightboxPrev.classList.toggle('hidden', !showArrows);
    if (lightboxNext) lightboxNext.classList.toggle('hidden', !showArrows);
    // Keep main carousel in sync
    setCarouselImage(lightboxIndex);
  }

  function openLightbox(index) {
    if (!lightbox || !images.length) return;
    // Move to body so it escapes any ancestor stacking context and covers the navbar
    if (lightbox.parentNode !== document.body) document.body.appendChild(lightbox);
    setLightboxImage(index != null ? index : currentImageIndex);
    lightbox.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    if (lightboxHint) {
      lightboxHint.classList.remove('hidden');
      clearTimeout(hintTimeout);
      hintTimeout = setTimeout(() => lightboxHint.classList.add('hidden'), 2500);
    }
  }

  function closeLightbox() {
    if (!lightbox) return;
    lightbox.style.display = 'none';
    lightbox.classList.remove('zoomed');
    document.body.style.overflow = '';
    clearTimeout(hintTimeout);
  }

  if (pdImageContainer) {
    pdImageContainer.addEventListener('click', function (e) {
      // Ignore clicks on the carousel prev/next buttons
      if (e.target.closest('.pd-carousel-btn')) return;
      // Ignore the click that follows a horizontal swipe
      if (mainSwiped) { mainSwiped = false; return; }
      openLightbox(currentImageIndex);
    });
  }

  if (lightboxClose) lightboxClose.addEventListener('click', function (e) {
    e.stopPropagation();
    closeLightbox();
  });
  if (lightboxPrev) lightboxPrev.addEventListener('click', function (e) {
    e.stopPropagation();
    setLightboxImage(lightboxIndex - 1);
  });
  if (lightboxNext) lightboxNext.addEventListener('click', function (e) {
    e.stopPropagation();
    setLightboxImage(lightboxIndex + 1);
  });

  // Click backdrop (outside the image) closes; click on the image toggles zoom
  if (lightbox) {
    lightbox.addEventListener('click', function (e) {
      if (e.target === lightboxImg) return; // handled below
      if (e.target.closest('.pd-lightbox-close, .pd-lightbox-prev, .pd-lightbox-next')) return; // handled by their own listeners
      if (!lightboxInner || !lightboxInner.contains(e.target)) closeLightbox();
    });
  }

  // Safety-net delegated handler: guarantees the close button works even if
  // the direct reference above is ever stale or the click target is a child
  // (e.g. text node) of the close button.
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('#pd-lightbox-close')) {
      closeLightbox();
    }
  });

  if (lightboxImg) {
    lightboxImg.addEventListener('click', function (e) {
      e.stopPropagation();
      if (!lightbox) return;
      const isZoomed = lightbox.classList.contains('zoomed');
      if (isZoomed) {
        lightbox.classList.remove('zoomed');
      } else {
        const rect = lightboxImg.getBoundingClientRect();
        const xPct = ((e.clientX - rect.left) / rect.width) * 100;
        const yPct = ((e.clientY - rect.top) / rect.height) * 100;
        lightboxImg.style.setProperty('--zoom-x', xPct + '%');
        lightboxImg.style.setProperty('--zoom-y', yPct + '%');
        lightbox.classList.add('zoomed');
      }
    });

    // Mobile: double-tap to zoom
    let lastTap = 0;
    lightboxImg.addEventListener('touchend', function (e) {
      const now = Date.now();
      if (now - lastTap < 300) {
        e.preventDefault();
        const touch = e.changedTouches[0];
        const rect = lightboxImg.getBoundingClientRect();
        const xPct = ((touch.clientX - rect.left) / rect.width) * 100;
        const yPct = ((touch.clientY - rect.top) / rect.height) * 100;
        lightboxImg.style.setProperty('--zoom-x', xPct + '%');
        lightboxImg.style.setProperty('--zoom-y', yPct + '%');
        lightbox.classList.toggle('zoomed');
      }
      lastTap = now;
    });
  }

  document.addEventListener('keydown', function (e) {
    if (!lightbox || lightbox.style.display !== 'flex') return;
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') setLightboxImage(lightboxIndex - 1);
    if (e.key === 'ArrowRight') setLightboxImage(lightboxIndex + 1);
  });

  // Mobile swipe-down to close, swipe left/right to navigate
  let touchStartY = null;
  let touchStartX = null;
  if (lightbox) {
    lightbox.addEventListener('touchstart', function (e) {
      if (lightbox.classList.contains('zoomed')) return;
      touchStartY = e.touches[0].clientY;
      touchStartX = e.touches[0].clientX;
    }, { passive: true });
    lightbox.addEventListener('touchend', function (e) {
      if (touchStartY == null || lightbox.classList.contains('zoomed')) return;
      const deltaY = e.changedTouches[0].clientY - touchStartY;
      const deltaX = touchStartX != null ? e.changedTouches[0].clientX - touchStartX : 0;
      if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
        setLightboxImage(lightboxIndex + (deltaX < 0 ? 1 : -1));
      } else if (deltaY > 80) {
        closeLightbox();
      }
      touchStartY = null;
      touchStartX = null;
    }, { passive: true });
  }

  // ---- Init ----
  renderSelectors();
  watchForSizeChart();

})();
